import React from 'react'
import { useDispatch } from 'react-redux'
import { useTable } from 'hooks'
import { getColumns } from './model'
import { toUserDepartments } from 'ducks/router'
import { fetchProjects } from 'api/projects'
import {
  postProjectAccess,
  deleteProjectAccess,
  postDepartmentProjectsAccess,
  deleteDepartmentProjectsAccess,
  fetchAllProjectsAccess,
} from 'api/users'
import type { TableQuery } from 'hooks/useTable'

const useUserProjectsTableMutations = () => {
  const [data, setData] = React.useState<any>(false)

  const handleFetchProjects = React.useCallback(
    async (id, { limit, offset, search }) => {
      const { response } = await fetchProjects({
        limit,
        offset,
        search,
        department: id,
      })

      setData(response)
    },
    [setData],
  )

  return {
    handleFetchProjects,
    setData,
    data,
  }
}

const useAllProjectsAccess = ({ updateTable, departmentId, user }) => {
  const [isAllProjectsAccess, setIsAllProjectsAccess] = React.useState(false)

  const handleFetchAllProjectsAccess = React.useCallback(
    async ({ user, departmentId }) => {
      const { response } = await fetchAllProjectsAccess({
        pk: user,
        departmentId,
      })

      if (response) {
        setIsAllProjectsAccess(response.has_access_to_all_projects)
      }
    },
    [setIsAllProjectsAccess],
  )

  const handleEditDepartmentProjectsAccess = React.useCallback(
    async ({ selected, user, departmentId }) => {
      const apiMethod = selected
        ? deleteDepartmentProjectsAccess
        : postDepartmentProjectsAccess

      await apiMethod({
        pk: user,
        departmentId,
      })

      updateTable()
      setIsAllProjectsAccess(!selected)
    },
    [setIsAllProjectsAccess, updateTable],
  )

  React.useEffect(() => {
    handleFetchAllProjectsAccess({ user, departmentId })
  }, [handleFetchAllProjectsAccess, departmentId, user])

  const toggleSelectAll = React.useCallback(() => {
    handleEditDepartmentProjectsAccess({
      selected: isAllProjectsAccess,
      user,
      departmentId,
    })
  }, [
    handleEditDepartmentProjectsAccess,
    isAllProjectsAccess,
    user,
    departmentId,
  ])

  return {
    isAllProjectsAccess,
    toggleSelectAll,
    handleFetchAllProjectsAccess,
  }
}

export const useUserProjectsTable = state => {
  const [signalTableUpdate, setSignalTableUpdate] = React.useState(false)

  const updateTable = () => {
    setSignalTableUpdate(!signalTableUpdate)
  }

  const { mapSortingPropToQueryParam, search, id, selectedUser, department } =
    state

  const user = selectedUser.id

  const columns = getColumns(department.name)

  const dispatch = useDispatch()

  const { handleFetchProjects, data, setData } = useUserProjectsTableMutations()

  const { isAllProjectsAccess, toggleSelectAll, handleFetchAllProjectsAccess } =
    useAllProjectsAccess({
      updateTable,
      departmentId: id,
      user,
    })

  const handleRefetch = React.useCallback(
    ({ limit, offset, search }: TableQuery) => {
      handleFetchProjects(id, { limit, offset, search })
    },
    [handleFetchProjects, id, signalTableUpdate],
  )

  const handleQueriesUpdate = React.useCallback(
    ({ limit, offset, search }: TableQuery) => {
      dispatch(
        toUserDepartments({
          pk: user,
          id: id,
          query: { limit, offset, search },
        }),
      )
    },
    [dispatch, user, id],
  )

  const handleSetAccess = React.useCallback(
    async ({ selected, projectId }) => {
      const apiMethod = selected ? deleteProjectAccess : postProjectAccess

      const { response } = await apiMethod({
        pk: user,
        departmentId: id,
        projectId,
      })

      // updating react state without refetching
      // data in order to get smooth UX
      if (response) {
        setData({
          ...data,
          results: data.results.map(project => {
            if (projectId === project.id) {
              return {
                ...project,
                members_with_access: selected
                  ? project.members_with_access.filter(
                      member => member.id !== user,
                    )
                  : [...project.members_with_access, selectedUser],
              }
            }

            return project
          }),
        })
      }

      handleFetchAllProjectsAccess({ user, departmentId: id })
    },
    [id, user, selectedUser, data, setData, handleFetchAllProjectsAccess],
  )

  const {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: { headers, model, isFetching },
    },
    handlers: {
      pagination: { handlePage, handlePageSize },
    },
  } = useTable({
    columns,
    data: data.results,
    mapSortingPropToQueryParam,
    pageCount: data.count,
    defaultPageSize: 7,
    search,
    fetchAction: handleRefetch,
    updateQueriesAction: handleQueriesUpdate,
  })

  return {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: { headers, model, isFetching, isAllProjectsAccess },
    },
    handlers: {
      pagination: { handlePage, handlePageSize },
      handleSetAccess,
      toggleSelectAll,
    },
  }
}
